import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Buildings,
  Receipt as ReceiptIcon,
  Bell,
  Monitor,
  CookingPot,
  FloppyDisk,
  ShieldCheck,
  Truck,
} from '@phosphor-icons/react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { API_URL as __API_BASE__ } from '@/lib/apiBase';
const API_URL = __API_BASE__;

import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
const CURRENCIES = [
  { v: 'USD', l: 'USD - US Dollar', sym: '$' },
  { v: 'EUR', l: 'EUR - Euro', sym: '€' },
  { v: 'GBP', l: 'GBP - British Pound', sym: '£' },
  { v: 'INR', l: 'INR - Indian Rupee', sym: '₹' },
  { v: 'AED', l: 'AED - UAE Dirham', sym: 'د.إ' },
  { v: 'AUD', l: 'AUD - Australian Dollar', sym: 'A$' },
  { v: 'CAD', l: 'CAD - Canadian Dollar', sym: 'C$' },
  { v: 'JPY', l: 'JPY - Japanese Yen', sym: '¥' },
];
const LANGUAGES = [
  { v: 'en', l: 'English' },
  { v: 'es', l: 'Español' },
  { v: 'fr', l: 'Français' },
  { v: 'de', l: 'Deutsch' },
  { v: 'ar', l: 'العربية' },
  { v: 'hi', l: 'हिन्दी' },
];
const TIMEZONES = [
  'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
  'Europe/London', 'Europe/Paris', 'Asia/Dubai', 'Asia/Kolkata', 'Asia/Tokyo',
  'Australia/Sydney', 'UTC',
];

// Re-usable row
const Row = ({ label, hint, children }) => (
  <div className="flex items-center justify-between gap-4 py-3 border-b border-[var(--theme-border)] last:border-b-0">
    <div className="min-w-0 flex-1">
      <p className="font-medium text-[var(--theme-text)]">{label}</p>
      {hint && <p className="text-xs text-[var(--theme-text-muted)] mt-0.5">{hint}</p>}
    </div>
    <div className="flex-shrink-0">{children}</div>
  </div>
);

export const Section = ({ icon: Icon, title, hint, children, testId }) => (
  <Card className="border border-[var(--theme-border)] bg-[var(--theme-surface)]" data-testid={testId}>
    <CardHeader className="border-b border-[var(--theme-border)]">
      <CardTitle className="flex items-center gap-2 text-lg text-[var(--theme-text)]">
        <Icon size={20} className="text-[var(--theme-accent)]" />
        {title}
      </CardTitle>
      {hint && <p className="text-xs text-[var(--theme-text-muted)] mt-1">{hint}</p>}
    </CardHeader>
    <CardContent className="p-5">{children}</CardContent>
  </Card>
);

/* =========================================================================
   useAppSettingsData — hoisted data hook so multiple tabs can share state
   ========================================================================= */
export const useAppSettingsData = (outletId) => {
  const [s, setS] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  // Permissions (Access Control tab)
  const [permCatalog, setPermCatalog] = useState([]);
  const [permRoles, setPermRoles] = useState([]);
  const [permMap, setPermMap] = useState({});
  const [permDirty, setPermDirty] = useState(false);
  const [permSaving, setPermSaving] = useState(false);

  const fetchSettings = useCallback(async () => {
    if (!outletId) return;
    try {
      const [r, p] = await Promise.all([
        axios.get(`${API_URL}/api/app-settings?outlet_id=${outletId}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/permissions?outlet_id=${outletId}`, { withCredentials: true }),
      ]);
      setS(r.data);
      setPermCatalog(p.data.catalog || []);
      setPermRoles(p.data.roles || []);
      setPermMap(p.data.permissions || {});
      setDirty(false);
      setPermDirty(false);
    } catch (e) { /* ignore */ }
  }, [outletId]);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const set = (section, key, value) => {
    setS((prev) => ({ ...prev, [section]: { ...prev[section], [key]: value } }));
    setDirty(true);
  };

  const togglePerm = (role, key) => {
    setPermMap((prev) => {
      const cur = new Set(prev[role] || []);
      if (cur.has(key)) cur.delete(key); else cur.add(key);
      return { ...prev, [role]: Array.from(cur) };
    });
    setPermDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/app-settings?outlet_id=${outletId}`, s, { withCredentials: true });
      toast.success('Settings saved');
      setDirty(false);
    } catch (e) {
      toast.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  const savePerms = async () => {
    setPermSaving(true);
    try {
      await axios.put(`${API_URL}/api/permissions?outlet_id=${outletId}`, permMap, { withCredentials: true });
      toast.success('Access control updated');
      setPermDirty(false);
    } catch (e) {
      toast.error('Failed to update access control');
    } finally {
      setPermSaving(false);
    }
  };

  return {
    s, set, dirty, saving, save,
    permCatalog, permRoles, permMap, togglePerm, permDirty, permSaving, savePerms,
  };
};

/* =========================================================================
   Save bar — shows global save buttons for both settings + permissions
   ========================================================================= */
export const SettingsSaveBar = ({ data }) => {
  const { dirty, saving, save, permDirty, permSaving, savePerms } = data;
  if (!dirty && !permDirty) return null;
  return (
    <div className="sticky top-2 z-30 flex justify-end gap-2 bg-[var(--theme-surface)]/95 backdrop-blur-sm border border-[var(--theme-accent)]/30 rounded-xl p-3 shadow-md">
      <span className="text-xs text-yellow-600 font-medium self-center mr-auto">
        You have unsaved changes
      </span>
      {permDirty && (
        <Button onClick={savePerms} disabled={permSaving} variant="outline" className="border-[var(--theme-accent)] text-[var(--theme-accent)]" data-testid="perms-save-btn">
          {permSaving ? 'Saving Access…' : 'Save Access Changes'}
        </Button>
      )}
      {dirty && (
        <Button
          onClick={save}
          disabled={saving}
          className="bg-[var(--theme-accent)] text-white"
          data-testid="settings-save-btn"
        >
          <FloppyDisk size={16} className="mr-2" />
          {saving ? 'Saving…' : 'Save Changes'}
        </Button>
      )}
    </div>
  );
};

/* =========================================================================
   Per-function content blocks
   ========================================================================= */
export const GeneralSettingsContent = ({ s, set }) => {
  if (!s) return <p className="text-sm text-[var(--theme-text-muted)]">Loading…</p>;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
      <Section icon={Buildings} title="Business Information" hint="Basic business details" testId="section-business">
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Application Name</Label>
            <Input value={s.general.app_name} onChange={(e) => set('general', 'app_name', e.target.value)} data-testid="general-app-name" />
          </div>
          <div>
            <Label className="text-xs">Business Name</Label>
            <Input value={s.general.business_name} onChange={(e) => set('general', 'business_name', e.target.value)} placeholder="My Restaurant" data-testid="general-business-name" />
          </div>
          <div>
            <Label className="text-xs">Language</Label>
            <Select value={s.general.language} onValueChange={(v) => set('general', 'language', v)}>
              <SelectTrigger data-testid="general-language"><SelectValue /></SelectTrigger>
              <SelectContent>
                {LANGUAGES.map((l) => <SelectItem key={l.v} value={l.v}>{l.l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </Section>

      <Section icon={Buildings} title="Currency & Tax" hint="Financial settings" testId="section-currency">
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Currency</Label>
            <Select value={s.general.currency} onValueChange={(v) => {
              const found = CURRENCIES.find((c) => c.v === v);
              set('general', 'currency', v);
              if (found) set('general', 'currency_symbol', found.sym);
            }}>
              <SelectTrigger data-testid="general-currency"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CURRENCIES.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Currency Symbol</Label>
            <Input value={s.general.currency_symbol} onChange={(e) => set('general', 'currency_symbol', e.target.value)} data-testid="general-currency-symbol" />
          </div>
          <div>
            <Label className="text-xs">Default Tax Rate (%)</Label>
            <Input type="number" step="0.001" value={s.general.default_tax_rate} onChange={(e) => set('general', 'default_tax_rate', parseFloat(e.target.value) || 0)} data-testid="general-tax-rate" />
          </div>
        </div>
      </Section>

      <Section icon={Buildings} title="Date & Time" hint="Regional settings" testId="section-datetime">
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Timezone</Label>
            <Select value={s.general.timezone} onValueChange={(v) => set('general', 'timezone', v)}>
              <SelectTrigger data-testid="general-timezone"><SelectValue /></SelectTrigger>
              <SelectContent>
                {TIMEZONES.map((tz) => <SelectItem key={tz} value={tz}>{tz}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Time Format</Label>
              <Select value={s.general.time_format} onValueChange={(v) => set('general', 'time_format', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="12h">12-hour</SelectItem>
                  <SelectItem value="24h">24-hour</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Date Format</Label>
              <Select value={s.general.date_format} onValueChange={(v) => set('general', 'date_format', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="MM/DD/YYYY">MM/DD/YYYY</SelectItem>
                  <SelectItem value="DD/MM/YYYY">DD/MM/YYYY</SelectItem>
                  <SelectItem value="YYYY-MM-DD">YYYY-MM-DD</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </Section>
    </div>
  );
};

export const ReceiptSettingsContent = ({ s, set }) => {
  if (!s) return <p className="text-sm text-[var(--theme-text-muted)]">Loading…</p>;
  return (
    <Section icon={ReceiptIcon} title="Receipt Customization" hint="Customize how receipts appear" testId="section-receipt">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Header Text</Label>
            <Textarea value={s.receipt.header_text} onChange={(e) => set('receipt', 'header_text', e.target.value)} rows={2} data-testid="receipt-header" />
          </div>
          <div>
            <Label className="text-xs">Footer Text</Label>
            <Textarea value={s.receipt.footer_text} onChange={(e) => set('receipt', 'footer_text', e.target.value)} rows={2} data-testid="receipt-footer" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Paper Size</Label>
              <Select value={s.receipt.paper_size} onValueChange={(v) => set('receipt', 'paper_size', v)}>
                <SelectTrigger data-testid="receipt-paper"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="58mm">58mm (Compact)</SelectItem>
                  <SelectItem value="80mm">80mm (Standard)</SelectItem>
                  <SelectItem value="A4">A4 (Invoice)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Font Size</Label>
              <Select value={s.receipt.font_size} onValueChange={(v) => set('receipt', 'font_size', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="small">Small</SelectItem>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="large">Large</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <div>
          <Row label="Show Logo" hint="Display logo on receipt">
            <Switch checked={s.receipt.show_logo} onCheckedChange={(v) => set('receipt', 'show_logo', v)} data-testid="receipt-show-logo" />
          </Row>
          <Row label="Show Store Address" hint="Include store address">
            <Switch checked={s.receipt.show_store_address} onCheckedChange={(v) => set('receipt', 'show_store_address', v)} data-testid="receipt-show-address" />
          </Row>
          <Row label="Show Store Phone" hint="Include store phone number">
            <Switch checked={s.receipt.show_store_phone} onCheckedChange={(v) => set('receipt', 'show_store_phone', v)} data-testid="receipt-show-phone" />
          </Row>
          <Row label="Show Cashier Name" hint="Include cashier name">
            <Switch checked={s.receipt.show_cashier_name} onCheckedChange={(v) => set('receipt', 'show_cashier_name', v)} data-testid="receipt-show-cashier" />
          </Row>
          <Row label="Show Tax Breakdown" hint="Itemize taxes on receipt">
            <Switch checked={s.receipt.show_tax_breakdown} onCheckedChange={(v) => set('receipt', 'show_tax_breakdown', v)} data-testid="receipt-show-tax" />
          </Row>
          <Row label="Order Status QR" hint="Print a QR code linking to live order status">
            <Switch checked={s.receipt.show_order_qr} onCheckedChange={(v) => set('receipt', 'show_order_qr', v)} data-testid="receipt-show-qr" />
          </Row>
        </div>
      </div>
    </Section>
  );
};

export const NotificationsSettingsContent = ({ s, set }) => {
  if (!s) return <p className="text-sm text-[var(--theme-text-muted)]">Loading…</p>;
  return (
    <Section icon={Bell} title="Notification Preferences" hint="Configure alerts and notifications" testId="section-notifications">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <Row label="Low Stock Alerts" hint="Get notified when stock is low">
            <Switch checked={s.notifications.low_stock_alerts} onCheckedChange={(v) => set('notifications', 'low_stock_alerts', v)} data-testid="notif-low-stock" />
          </Row>
          <div className="py-3 border-b border-[var(--theme-border)]">
            <Label className="text-xs">Low Stock Threshold</Label>
            <Input type="number" value={s.notifications.low_stock_threshold} onChange={(e) => set('notifications', 'low_stock_threshold', parseInt(e.target.value) || 0)} data-testid="notif-threshold" />
          </div>
          <Row label="Daily Sales Summary" hint="Receive daily sales report">
            <Switch checked={s.notifications.daily_sales_summary} onCheckedChange={(v) => set('notifications', 'daily_sales_summary', v)} data-testid="notif-daily" />
          </Row>
        </div>
        <div>
          <Row label="Shift End Reminder" hint="Remind to close shift">
            <Switch checked={s.notifications.shift_end_reminder} onCheckedChange={(v) => set('notifications', 'shift_end_reminder', v)} data-testid="notif-shift-end" />
          </Row>
          <Row label="Email Notifications" hint="Receive email alerts">
            <Switch checked={s.notifications.email_notifications} onCheckedChange={(v) => set('notifications', 'email_notifications', v)} data-testid="notif-email" />
          </Row>
          <Row label="SMS Notifications" hint="Receive SMS alerts (requires Twilio)">
            <Switch checked={s.notifications.sms_notifications} onCheckedChange={(v) => set('notifications', 'sms_notifications', v)} data-testid="notif-sms" />
          </Row>
          <div className="py-3">
            <Label className="text-xs">Webhook URL</Label>
            <Input value={s.notifications.webhook_url} onChange={(e) => set('notifications', 'webhook_url', e.target.value)} placeholder="https://hooks.slack.com/..." data-testid="notif-webhook" />
          </div>
        </div>
      </div>
    </Section>
  );
};

export const POSSettingsContent = ({ s, set }) => {
  if (!s) return <p className="text-sm text-[var(--theme-text-muted)]">Loading…</p>;
  return (
    <Section icon={Monitor} title="POS Terminal Settings" hint="Configure point of sale behaviour" testId="section-pos">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Default Payment Method</Label>
            <Select value={s.pos.default_payment_method} onValueChange={(v) => set('pos', 'default_payment_method', v)}>
              <SelectTrigger data-testid="pos-default-payment"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="cash">Cash</SelectItem>
                <SelectItem value="card">Card</SelectItem>
                <SelectItem value="online">Online</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Default Order Type</Label>
            <Select value={s.pos.default_order_type} onValueChange={(v) => set('pos', 'default_order_type', v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="dine_in">Dine-in</SelectItem>
                <SelectItem value="takeaway">Takeaway</SelectItem>
                <SelectItem value="delivery">Delivery</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Row label="Require Customer" hint="Customer required for sale">
            <Switch checked={s.pos.require_customer} onCheckedChange={(v) => set('pos', 'require_customer', v)} data-testid="pos-require-customer" />
          </Row>
          <Row label="Auto Print Receipt" hint="Print receipt automatically">
            <Switch checked={s.pos.auto_print_receipt} onCheckedChange={(v) => set('pos', 'auto_print_receipt', v)} data-testid="pos-auto-print" />
          </Row>
        </div>
        <div>
          <Row label="Allow Negative Inventory" hint="Allow sales when out of stock">
            <Switch checked={s.pos.allow_negative_inventory} onCheckedChange={(v) => set('pos', 'allow_negative_inventory', v)} data-testid="pos-neg-inv" />
          </Row>
          <Row label="Quick Sale Mode" hint="Enable fast checkout">
            <Switch checked={s.pos.quick_sale_mode} onCheckedChange={(v) => set('pos', 'quick_sale_mode', v)} data-testid="pos-quick-sale" />
          </Row>
          <Row label="Enable Tips" hint="Allow tip collection">
            <Switch checked={s.pos.enable_tips} onCheckedChange={(v) => set('pos', 'enable_tips', v)} data-testid="pos-tips" />
          </Row>
          <Row label="Auto-collapse Modifiers" hint="Hide modifier groups after selection">
            <Switch checked={s.pos.auto_collapse_modifiers} onCheckedChange={(v) => set('pos', 'auto_collapse_modifiers', v)} data-testid="pos-collapse-mod" />
          </Row>
          <div className="py-3">
            <Label className="text-xs">Tip Presets (comma-separated %)</Label>
            <Input
              value={(s.pos.tip_presets || []).join(',')}
              onChange={(e) => set('pos', 'tip_presets',
                e.target.value.split(',').map((x) => parseInt(x.trim())).filter(Number.isFinite))}
              data-testid="pos-tip-presets"
            />
          </div>
        </div>
      </div>
    </Section>
  );
};

export const KDSSettingsContent = ({ s, set }) => {
  if (!s) return <p className="text-sm text-[var(--theme-text-muted)]">Loading…</p>;
  return (
    <Section icon={CookingPot} title="Kitchen Display Settings" hint="Configure kitchen display system" testId="section-kds">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <Row label="Auto Accept Orders" hint="Automatically start new orders">
            <Switch checked={s.kds.auto_accept_orders} onCheckedChange={(v) => set('kds', 'auto_accept_orders', v)} data-testid="kds-auto-accept" />
          </Row>
          <Row label="Sound Enabled" hint="Play sound for new orders">
            <Switch checked={s.kds.sound_enabled} onCheckedChange={(v) => set('kds', 'sound_enabled', v)} data-testid="kds-sound" />
          </Row>
          <div className="py-3 border-b border-[var(--theme-border)]">
            <Label className="text-xs">Sound Volume ({s.kds.sound_volume}%)</Label>
            <input
              type="range" min="0" max="100"
              value={s.kds.sound_volume}
              onChange={(e) => set('kds', 'sound_volume', parseInt(e.target.value))}
              className="w-full"
              data-testid="kds-volume"
            />
          </div>
          <div className="py-3">
            <Label className="text-xs">Auto Refresh (seconds)</Label>
            <Input type="number" min="5" value={s.kds.auto_refresh_seconds} onChange={(e) => set('kds', 'auto_refresh_seconds', parseInt(e.target.value) || 30)} data-testid="kds-refresh" />
          </div>
        </div>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Warning Time (minutes)</Label>
            <Input type="number" min="1" value={s.kds.warning_minutes} onChange={(e) => set('kds', 'warning_minutes', parseInt(e.target.value) || 10)} data-testid="kds-warning" />
            <p className="text-[10px] text-[var(--theme-text-muted)] mt-1">Time before order shows warning</p>
          </div>
          <div>
            <Label className="text-xs">Critical Time (minutes)</Label>
            <Input type="number" min="1" value={s.kds.critical_minutes} onChange={(e) => set('kds', 'critical_minutes', parseInt(e.target.value) || 15)} data-testid="kds-critical" />
            <p className="text-[10px] text-[var(--theme-text-muted)] mt-1">Time before order shows critical</p>
          </div>
          <Row label="Show Prep Estimate" hint="Display estimated prep time on each ticket">
            <Switch checked={s.kds.show_prep_estimate} onCheckedChange={(v) => set('kds', 'show_prep_estimate', v)} data-testid="kds-prep-estimate" />
          </Row>
        </div>
      </div>
    </Section>
  );
};

export const DeliverySettingsContent = ({ s, set }) => {
  if (!s) return <p className="text-sm text-[var(--theme-text-muted)]">Loading…</p>;
  const d = s.delivery || {};
  const sym = s.general?.currency_symbol || '$';
  return (
    <Section icon={Truck} title="Delivery Pricing & Zone" hint="Configure delivery fees, free range, and the maximum service distance" testId="section-delivery">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Base Delivery Fee ({sym})</Label>
            <Input
              type="number" min="0" step="0.5"
              value={d.base_fee ?? 3}
              onChange={(e) => set('delivery', 'base_fee', parseFloat(e.target.value) || 0)}
              data-testid="delivery-base-fee"
            />
            <p className="text-[10px] text-[var(--theme-text-muted)] mt-1">Flat fee added to every delivery order.</p>
          </div>
          <div>
            <Label className="text-xs">Per-mile Fee ({sym}/mi)</Label>
            <Input
              type="number" min="0" step="0.25"
              value={d.per_mile_fee ?? 1.5}
              onChange={(e) => set('delivery', 'per_mile_fee', parseFloat(e.target.value) || 0)}
              data-testid="delivery-per-mile-fee"
            />
            <p className="text-[10px] text-[var(--theme-text-muted)] mt-1">Charged for every mile beyond the free range.</p>
          </div>
          <div>
            <Label className="text-xs">Minimum Order Total ({sym})</Label>
            <Input
              type="number" min="0" step="1"
              value={d.min_order_total ?? 0}
              onChange={(e) => set('delivery', 'min_order_total', parseFloat(e.target.value) || 0)}
              data-testid="delivery-min-order"
            />
          </div>
        </div>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Free Distance (mi)</Label>
            <Input
              type="number" min="0" step="0.25"
              value={d.free_under_mi ?? 1.5}
              onChange={(e) => set('delivery', 'free_under_mi', parseFloat(e.target.value) || 0)}
              data-testid="delivery-free-mi"
            />
            <p className="text-[10px] text-[var(--theme-text-muted)] mt-1">Below this distance only the base fee applies.</p>
          </div>
          <div>
            <Label className="text-xs">Maximum Delivery Distance (mi)</Label>
            <Input
              type="number" min="0.5" step="0.5"
              value={d.max_mi ?? 6}
              onChange={(e) => set('delivery', 'max_mi', parseFloat(e.target.value) || 0)}
              data-testid="delivery-max-mi"
            />
            <p className="text-[10px] text-[var(--theme-text-muted)] mt-1">Orders beyond this distance are blocked in POS.</p>
          </div>

          <div className="rounded-lg bg-[var(--theme-accent)]/5 border border-[var(--theme-accent)]/30 p-3 text-xs space-y-1 mt-2">
            <p className="font-semibold text-[var(--theme-accent)]">Quick formula</p>
            <p className="font-mono text-[var(--theme-text)]">
              fee = {sym}{(d.base_fee ?? 0).toFixed(2)}
              {(d.per_mile_fee ?? 0) > 0 ? ` + (distance − ${d.free_under_mi ?? 0} mi) × ${sym}${(d.per_mile_fee ?? 0).toFixed(2)}` : ''}
            </p>
            <p className="text-[var(--theme-text-muted)]">Example at 3 mi → {sym}{
              (Number(d.base_fee || 0) + Math.max(0, 3 - Number(d.free_under_mi || 0)) * Number(d.per_mile_fee || 0)).toFixed(2)
            }</p>
          </div>
        </div>
      </div>
    </Section>
  );
};

export const AccessControlContent = ({ permCatalog, permRoles, permMap, togglePerm, permDirty }) => (
  <Section icon={ShieldCheck} title="Role-Based Access Control" hint="Grant or revoke permissions per role. Changes apply to all users with that role at this outlet." testId="section-access">
    <p className="text-xs text-[var(--theme-text-muted)] mb-4">
      Tip: These permissions also control which neon cards appear on the dashboard for each role.
      Example — to make cooks only see KDS, leave just <span className="font-mono">kds.update_status</span> + <span className="font-mono">orders.view</span> checked for <span className="font-semibold">kitchen_staff</span> and uncheck all other permission groups.
    </p>

    <div className="rounded-lg border border-[var(--theme-accent)]/30 bg-[var(--theme-accent)]/5 p-3 mb-4 text-xs text-[var(--theme-text)] grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1">
      <div><span className="font-mono text-[var(--theme-accent)]">orders.create</span> → POS card</div>
      <div><span className="font-mono text-[var(--theme-accent)]">kds.update_status</span> → KDS card</div>
      <div><span className="font-mono text-[var(--theme-accent)]">tables.manage</span> → Tables card</div>
      <div><span className="font-mono text-[var(--theme-accent)]">orders.view</span> → Orders card</div>
      <div><span className="font-mono text-[var(--theme-accent)]">menu.view</span> → Menu card</div>
      <div><span className="font-mono text-[var(--theme-accent)]">inventory.view</span> → Inventory card</div>
      <div><span className="font-mono text-[var(--theme-accent)]">customers.view</span> → Customers card</div>
      <div><span className="font-mono text-[var(--theme-accent)]">reports.view</span> → Reports card</div>
      <div><span className="font-mono text-[var(--theme-accent)]">users.manage</span> → Users card</div>
      <div><span className="font-mono text-[var(--theme-accent)]">settings.edit</span> → Settings card</div>
    </div>

    <div className="overflow-x-auto rounded-xl border border-[var(--theme-border)]" data-testid="access-matrix">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-[var(--theme-background)] border-b border-[var(--theme-border)]">
            <th className="text-left p-3 font-semibold text-[var(--theme-text)] sticky left-0 bg-[var(--theme-background)] z-10">Permission</th>
            {permRoles.map((role) => (
              <th key={role} className="p-3 text-xs font-semibold text-[var(--theme-text)] capitalize whitespace-nowrap text-center" data-testid={`access-role-col-${role}`}>
                {role.replace(/_/g, ' ')}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from(new Set(permCatalog.map((p) => p.group))).map((groupName) => (
            <React.Fragment key={groupName}>
              <tr className="bg-[var(--theme-background)]/50">
                <td colSpan={permRoles.length + 1} className="px-3 py-1.5 text-[10px] uppercase tracking-widest font-semibold text-[var(--theme-text-muted)]">
                  {groupName}
                </td>
              </tr>
              {permCatalog.filter((p) => p.group === groupName).map((perm) => (
                <tr key={perm.key} className="border-t border-[var(--theme-border)]/50 hover:bg-[var(--theme-background)]/30" data-testid={`access-row-${perm.key}`}>
                  <td className="p-3 sticky left-0 bg-[var(--theme-surface)]">
                    <p className="font-medium text-[var(--theme-text)]">{perm.label}</p>
                    <p className="text-[10px] text-[var(--theme-text-muted)] font-mono">{perm.key}</p>
                  </td>
                  {permRoles.map((role) => {
                    const checked = (permMap[role] || []).includes(perm.key);
                    const isPrivileged = ['super_admin', 'company_admin'].includes(role);
                    return (
                      <td key={role} className="p-3 text-center">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => togglePerm(role, perm.key)}
                          disabled={isPrivileged}
                          className="w-4 h-4 accent-[var(--theme-accent)] cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                          title={isPrivileged ? `${role} always has all permissions` : `Toggle ${perm.key} for ${role}`}
                          data-testid={`access-cell-${role}-${perm.key}`}
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>

    <div className="flex items-center justify-between mt-4 text-xs text-[var(--theme-text-muted)]">
      <span>{permCatalog.length} permissions × {permRoles.length} roles</span>
      {permDirty && <span className="text-yellow-600 font-medium">Unsaved changes — click "Save Access Changes" at the top.</span>}
    </div>
  </Section>
);

/* =========================================================================
   Original AppSettingsPanel kept for backwards compatibility (unused now)
   ========================================================================= */
export const AppSettingsPanel = ({ outletId }) => {
  const data = useAppSettingsData(outletId);
  const { s } = data;
  if (!s) return <p className="text-sm text-[var(--theme-text-muted)]">Loading settings…</p>;
  return (
    <Card className="border border-[var(--theme-border)] bg-[var(--theme-surface)]" data-testid="app-settings-panel">
      <CardContent className="p-5 space-y-5">
        <SettingsSaveBar data={data} />
        <GeneralSettingsContent {...data} />
        <ReceiptSettingsContent {...data} />
        <NotificationsSettingsContent {...data} />
        <POSSettingsContent {...data} />
        <KDSSettingsContent {...data} />
        <AccessControlContent {...data} />
      </CardContent>
    </Card>
  );
};

export default AppSettingsPanel;
